import type { PublishedPortalSnapshotRecord, PublishedPortalWeekMatchRecord, PublishedPortalWeekRecord } from '../api/types.js';
import { formatDate } from '../lib/formatting.js';
import { GuestPortalScorecard, PortalWeekSkinsTable } from './pagesGuestPortal.js';

function formatCourseHandicap(courseHandicap: number | null): string | null {
  return typeof courseHandicap === 'number' && Number.isFinite(courseHandicap) ? `${Math.round(courseHandicap)}` : null;
}

function normalizeDisplayName(name: string): string {
  return name
    .replace(/[\u200B-\u200D\u2060\uFEFF]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeDisplayNameForRecap(name: string): string {
  return normalizeDisplayName(name).replace(/ /g, '\u00A0');
}

function formatPlayerWithCourseHandicap(name: string, courseHandicap: number | null): string {
  const normalizedName = normalizeDisplayNameForRecap(name);
  const formattedHandicap = formatCourseHandicap(courseHandicap);
  return formattedHandicap ? `${normalizedName} (${formattedHandicap})` : normalizedName;
}

function getStrokeAllowanceNote(match: PublishedPortalWeekMatchRecord): {
  playerANote: string | null;
  playerBNote: string | null;
} {
  const playerAHandicap =
    typeof match.playerACourseHandicap === 'number' && Number.isFinite(match.playerACourseHandicap)
      ? Math.round(match.playerACourseHandicap)
      : null;
  const playerBHandicap =
    typeof match.playerBCourseHandicap === 'number' && Number.isFinite(match.playerBCourseHandicap)
      ? Math.round(match.playerBCourseHandicap)
      : null;

  if (playerAHandicap === null || playerBHandicap === null) {
    return { playerANote: null, playerBNote: null };
  }

  const strokeDifference = Math.abs(playerAHandicap - playerBHandicap);
  if (strokeDifference === 0) {
    return { playerANote: null, playerBNote: null };
  }

  const note = `Gets ${strokeDifference} stroke${strokeDifference === 1 ? '' : 's'}`;
  return playerAHandicap > playerBHandicap
    ? { playerANote: note, playerBNote: null }
    : { playerANote: null, playerBNote: note };
}

function SchedulePairingRow({ match }: { match: PublishedPortalWeekMatchRecord }) {
  const { playerANote, playerBNote } = getStrokeAllowanceNote(match);
  return (
    <tr>
      <td>
        <strong>{formatPlayerWithCourseHandicap(match.playerAName, match.playerACourseHandicap)}</strong>
        {playerANote ? <div className="muted">{playerANote}</div> : null}
      </td>
      <td>
        <strong>{formatPlayerWithCourseHandicap(match.playerBName, match.playerBCourseHandicap)}</strong>
        {playerBNote ? <div className="muted">{playerBNote}</div> : null}
      </td>
    </tr>
  );
}

export function PortalWeeklyRecapExport({
  snapshot,
  selectedWeek,
  nextWeek,
}: {
  snapshot: PublishedPortalSnapshotRecord;
  selectedWeek: PublishedPortalWeekRecord;
  nextWeek: PublishedPortalWeekRecord | null;
}) {
  return (
    <div className="weekly-recap-export">
      <header className="weekly-recap-export-hero">
        <div className="weekly-recap-export-eyebrow">Weekly League Recap</div>
        <h2 className="weekly-recap-export-title">{snapshot.league_name}</h2>
        <p className="weekly-recap-export-subtitle">
          {snapshot.season_name} | Published {formatDate(snapshot.published_at)}
        </p>
      </header>

      <div className="weekly-recap-export-summary-row">
        <section className="weekly-recap-export-section">
          <div className="weekly-recap-export-section-title">Updated Standings</div>
          <div className="table-wrap">
            <table className="data-table weekly-recap-export-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Player</th>
                  <th>Total Points</th>
                </tr>
              </thead>
              <tbody>
                {snapshot.standings.map((row, index) => (
                  <tr key={row.playerId}>
                    <td>{index + 1}</td>
                    <td>{row.playerName}</td>
                    <td>{row.totalPoints}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="weekly-recap-export-section">
          <div className="weekly-recap-export-section-title">Next Week Schedule</div>
          {nextWeek ? (
            <>
              <div className="weekly-recap-export-meta">
                Week {nextWeek.weekNumber} | {formatDate(nextWeek.date)} | {nextWeek.courseName} |{' '}
                {nextWeek.hammerHole ? `Hammer hole ${nextWeek.hammerHole}` : 'No hammer hole'}
              </div>
              <div className="table-wrap">
                <table className="data-table weekly-recap-export-table">
                  <thead>
                    <tr>
                      <th>Player A</th>
                      <th>Player B</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nextWeek.matches.map((match) => (
                      <SchedulePairingRow key={match.id} match={match} />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="weekly-recap-export-empty">No later published week scheduled yet.</div>
          )}
        </section>

        <section className="weekly-recap-export-section">
          <div className="weekly-recap-export-section-title">Week {selectedWeek.weekNumber} Results</div>
          <div className="weekly-recap-export-meta">
            {formatDate(selectedWeek.date)} | {selectedWeek.courseName} |{' '}
            {selectedWeek.hammerHole ? `Hammer hole ${selectedWeek.hammerHole}` : 'No hammer hole'}
          </div>
          <div className="table-wrap">
            <table className="data-table weekly-recap-export-table">
              <thead>
                <tr>
                  <th>Matchup</th>
                  <th>Points</th>
                </tr>
              </thead>
              <tbody>
                {selectedWeek.matches.map((match) => (
                  <tr key={match.id}>
                    <td>
                      {formatPlayerWithCourseHandicap(match.playerAName, match.playerACourseHandicap)} vs.{' '}
                      {formatPlayerWithCourseHandicap(match.playerBName, match.playerBCourseHandicap)}
                    </td>
                    <td>
                      {match.playerAPoints} - {match.playerBPoints}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div className="weekly-recap-export-skins-row">
        <PortalWeekSkinsTable week={selectedWeek} className="weekly-recap-export-section weekly-recap-export-skins-panel" />
      </div>

      <div className="weekly-recap-export-scorecards-row">
        <div className="weekly-recap-export-scorecards">
          {selectedWeek.scorecards.map((scorecard) => (
            <GuestPortalScorecard
              key={scorecard.id}
              scorecard={scorecard}
              condensed
              hideHandicapRow
              presentation="recap-export"
            />
          ))}
        </div>
      </div>
    </div>
  );
}
